import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { getArticle } from "@/articles";
import { useSeo } from "@/hooks/use-seo";
import { Navbar, Footer } from "@/pages/Home";

export default function BlogPost() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useSeo(
    article
      ? { title: `${article.title} | Cabin Crew Mentoring`, description: article.excerpt, path: `/blog/${article.slug}` }
      : { title: "Article | Cabin Crew Mentoring", path: "/blog" }
  );

  if (!article) return <Navigate to="/blog" replace />;

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <article className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <Link to="/blog" data-testid="article-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:underline">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex flex-wrap items-center gap-3 text-xs mt-8">
              <span className="rounded-full bg-sky-100 text-sky-800 font-bold px-3 py-1 uppercase tracking-wider">{article.category}</span>
              <span className="text-slate-400">{article.date}</span>
              <span className="text-slate-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {article.readTime}</span>
            </div>
            <h1 data-testid="article-title" className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F] mt-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-slate-500 mt-4 text-sm">By Tony — former flight attendant (Canada &amp; Australia) and former airline hiring team member</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-10 space-y-10"
            data-testid="article-body"
          >
            {article.content.map((section, i) => (
              <section key={i}>
                <h2 className="text-xl sm:text-2xl font-bold text-[#0A192F]">{section.h}</h2>
                {section.paragraphs?.map((p) => (
                  <p key={p.slice(0, 40)} className="text-slate-600 leading-relaxed mt-4">{p}</p>
                ))}
                {section.list && (
                  <ul className="mt-4 space-y-2.5">
                    {section.list.map((item) => (
                      <li key={item.slice(0, 40)} className="text-slate-600 leading-relaxed flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-2 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {section.paragraphs_after?.map((p) => (
                  <p key={p.slice(0, 40)} className="text-slate-600 leading-relaxed mt-4">{p}</p>
                ))}
              </section>
            ))}
          </motion.div>

          <div className="mt-16 navy-panel rounded-2xl p-8 sm:p-10 text-white flex flex-col sm:flex-row items-center gap-6 justify-between">
            <div>
              <h3 className="font-display uppercase text-2xl font-bold">Assessment day coming up?</h3>
              <p className="text-slate-300 text-sm mt-2 max-w-md">Run the hard version with Tony first — a real mock interview, scored the way the panel scores it.</p>
            </div>
            <Link
              to="/book?session=interview_ready"
              data-testid="article-cta-btn"
              className="shrink-0 rounded-full bg-[#F59E0B] text-[#0A192F] font-bold px-7 py-3.5 hover:bg-amber-400 transition-colors duration-200 flex items-center gap-2"
            >
              Book Interview Prep <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}
